<template>
    <v-layout row>
        <v-flex xs12 sm12>
            <v-card class="elevation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    <v-btn dark icon title="Back to previous page" @click="$router.go(-1)">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <help-link appId="finance" pageId="loan-self-service" color="white"></help-link>
                    <v-btn v-if="hasAddRights" fab dark small color="primary" title="Add Loan Payments" @click="showAdd">
                        <v-icon dark>add</v-icon>
                    </v-btn>
                </v-toolbar>
            </v-card>
            <v-card class="elevation-1 mt-5"> 
                <v-toolbar flat height="45px">
                    <div :class="isMobile ? 'wp-60 mb-10' : 'wp-30 mb-10'">
                        <v-text-field v-model="search" append-icon="search" label="Search loan type, status" single-line hide-details clearable></v-text-field>
                    </div>
                    <v-spacer></v-spacer>
                </v-toolbar>
                <v-data-table
                   :headers="isMobile ? [] : headers"
                    :items="loans_summary_filtered"
                    :loading="loading"
                    :rows-per-page-items="rowsPerPageItems"
                    :pagination.sync="paginationSync"
                    :hide-headers="isMobile"
                >
                    <v-progress-linear v-slot:progress color="primary" indeterminate></v-progress-linear>
                    <template v-slot:items="props">

                        <!-- mobile view -->
                        <tr v-if="isMobile" @click="viewDetails(props.item.loanId)">
                            <td :colspan="headers.length" class="pa-1">
                                <v-card class="elevation-1">
                                    <v-layout row wrap align-center class="section-layout">
                                        <v-flex xs12 class="text-xs-left mt-5 mb-2">
                                            <v-layout row>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Loan Type</div>
                                                    <div>{{ props.item.loanType }}</div>
                                                </v-flex>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Payment Start Date</div>
                                                    <div v-if="props.item.paymentSchedule.length">{{ props.item.paymentStartDate | moment('MMM DD, YYYY') }}</div>
                                                </v-flex>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Last Payment Date</div>
                                                    <div v-if="props.item.paymentSchedule.length">{{ props.item.lastPaymentDate | moment('MMM DD, YYYY') }}</div>
                                                </v-flex>
                                            </v-layout>
                                            <v-layout row>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Total Amount to Pay</div>
                                                    <div>₱{{ numFormat.formatNumber(props.item.totalDue) }}</div>
                                                </v-flex>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Total Amount Paid</div>
                                                    <div>₱{{ numFormat.formatNumber(getTotalPaid(props.item.paymentSchedule)) }}</div>
                                                </v-flex>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Status</div>
                                                    <div :class="commonfn.getStatusColor(props.item.status)">{{commonfn.getStatusDescription(props.item.status)}}</div>
                                                </v-flex>
                                            </v-layout>
                                        </v-flex>
                                    </v-layout>
                                </v-card>
                            </td>
                        </tr>

                        <!-- desktop view -->
                        <tr v-else @click="viewDetails(props.item.loanId)" title="Click to view payment schedule" class="clickable">
                            <td>{{ props.item.loanType }}</td>
                            <td>{{ props.item.paymentStartDate | moment('MMM DD, YYYY') }}</td>
                            <td>{{ props.item.lastPaymentDate | moment('MMM DD, YYYY') }}</td>
                            <td>₱{{ numFormat.formatNumber(props.item.totalDue) }}</td>
                            <td>₱{{ numFormat.formatNumber(getTotalPaid(props.item.paymentSchedule)) }}</td>
                            <td>
                                <b :class="commonfn.getStatusColor(props.item.status)">{{commonfn.getStatusDescription(props.item.status)}}</b>
                            </td>
                        </tr>
                    </template>
                </v-data-table> 
            </v-card>
        </v-flex>
        <v-dialog v-model="dialogLoans" width="700" persistent>   
            <add-edit-loan ref="loansRef" :dialogLoans.sync="dialogLoans" @send="viewDetails"></add-edit-loan>
        </v-dialog>
    </v-layout> 
</template>

<script>

import commonfn from '@/common/functions'   //for refactor
import loansApi from '@/api/loans'
import numFormat from '@/common/num_format'
import pageKeys from '@/common/pagekeys'
import rowsPerPageItems from '@/constants/rows-per-page'
import { getSort, getRowsPerPage, getSearchKeyword, updateRowsPerPage, updateSort, updateSearchKeyword } from '@/helpers/user-prefs'
import { hasAddRights } from '@/helpers/access-permissions'

export default {     
    name: 'MyLoans',
    metaInfo: {
        title: 'My Loan Payments',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'View, create Loans' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
        ]
    },
    components: {
        'help-link': () => import('@/components/Common/HelpLink'),
        'add-edit-loan': () => import('@/components/Loans/AddEditLoans')
    },   
    data () {
        const routeName = this.$route.name
        const savedSort = getSort(routeName)

        return {
            commonfn,
            numFormat,
            rowsPerPageItems,
            paginationSync: {
                rowsPerPage: getRowsPerPage(routeName),
                sortBy: savedSort.sortBy,
                descending: savedSort.descending,
            },
            search: getSearchKeyword(routeName),
            error: false,
            errorMessage: '',
            loading: false,
            dialogLoans: false,
            headers: [
                {text: 'Loan Type',align: 'left',value: 'loanType', width: '15%', sortable:true},
                {text: 'Payment Start Date',align: 'left',value: 'paymentStartDate',  width: '15%', sortable:true},
                {text: 'Last Payment Date',align: 'left',value: 'lastPaymentDate',  width: '15%', sortable:true},
                {text: 'Total Amount to Pay',align: 'left',value: 'totalDue',  width: '15%', sortable:true},
                {text: 'Total Amount Paid',align: 'left',value: 'paymentSchedule',  width: '15%', sortable:true},
                {text: 'Status',align: 'left',value: '',  width: '15%', sortable:true},
            ],
            loans: [],
        }
    },
    methods: {
        showAdd() {
            const data = {
                isRequestForOtherUser: false,
                pageKey: pageKeys.my_loans
            }
            this.$refs.loansRef.setAdd(data)
            this.dialogLoans = true
        },
        getTotalPaid(data){
            var paidSchedule = data.filter(x => {
                return x.paidOn != null && x.status == commonfn.getStatusCode('Paid')
            })
            if (paidSchedule.length>0) return paidSchedule.map(x => x.amountDue).reduce((prev,next) => prev+next)
            else return 0
        },
        viewUserProfile(data, loggedInUserId) {
            if (data.userId == loggedInUserId) this.$router.push(`/myprofile`)
            else this.$router.push(`/hr/employees/${data.userId}`)
        },
        viewDetails(loanId) {
            this.$router.push(`/finance/myloans/${loanId}`)
        },
        async getMyLoans(show_loading) {
            try {
                this.loading = show_loading
                const response = await loansApi.getMyLoans()
                if (response.status == 200) {
                    this.loans = response.data.items
                }
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        }
    },
    created() {
        //update header titles
        this.$root.$emit('setHeaderTitles', {
            display: true,
            headerTitle: '',
            headerPageTitle: 'My Loan Payments'
        })

        this.getMyLoans(true)
    },
    watch: {
        'paginationSync.rowsPerPage'(newVal) {
            updateRowsPerPage(this.$route.name, newVal)
        },
        'paginationSync.sortBy'(newVal) {
            updateSort(this.$route.name, newVal, this.paginationSync.descending)
        },
        'paginationSync.descending'(newVal) {
            updateSort(this.$route.name, this.paginationSync.sortBy, newVal)
        },
        search(newVal) {
            updateSearchKeyword(this.$route.name, newVal)
        }
    },
    computed: {
        hasAddRights() {
            return hasAddRights(this.$router.currentRoute.path)
        },
        loans_summary_filtered() {
            var temp = this.loans
            
            if (this.search) {
                temp = temp.filter(item => {
                    const searchLower = this.search.toLowerCase()
                    const typeMatch = item.loanType.toLowerCase().includes(searchLower)
                    const statusMatch = commonfn.getStatusDescription(item.status).toLowerCase().includes(searchLower)

                    return typeMatch || statusMatch
                })
            }

            // Add computed fields
            temp = temp.map(e => {
                const hasSchedule = Array.isArray(e.paymentSchedule) && e.paymentSchedule.length > 0;
                const paymentStartDate = hasSchedule ? e.paymentSchedule[0].dueOn : null;
                const lastPaymentDate = hasSchedule ? e.paymentSchedule[e.paymentSchedule.length - 1].dueOn : null;

                return {
                    ...e,
                    paymentStartDate,
                    lastPaymentDate,
                    Status: e.paidOn ? 'Paid' : 'Unpaid'
                }
            })
            return temp
        },
    }
}
</script>
<style scoped>
.section-layout {
    padding:0px 23px 0px 23px;
}
</style>